/**
 * MyRetakesCard — dashboard widget for foremen/technicians showing
 * every photo they uploaded that got rejected. Deep-links straight to
 * the project so field crew can start reshooting without hunting.
 *
 * Silent-when-empty: the widget hides itself if the user has zero
 * rejected photos, so admins/PMs (who typically don't upload photos)
 * don't see a "You have 0 retakes" tile cluttering their dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldX, Camera, ChevronRight, X as XIcon } from "lucide-react";

// Format an ISO timestamp as a compact "N days ago" / "just now" so
// the card reads at a glance. Falls back to raw string on parse fail.
function relTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - then;
    const s = Math.round(diffMs / 1000);
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.round(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.round(mo / 12)}y ago`;
  } catch {
    return iso;
  }
}

// Per-device dismiss key. Once a user clicks the X, we hide the whole
// card for the current session so they can focus on the rest of the
// dashboard until they refresh. Not a permanent-forget — a hard
// refresh brings it back so retakes don't get silently forgotten.
const DISMISS_KEY = "f2_my_retakes_dismissed";

export default function MyRetakesCard() {
  const [items, setItems] = useState(null); // null = loading; [] = loaded-empty
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    api.get("/photos/my-retakes?limit=8")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  // Loading OR empty → render nothing. Dashboard stays clean for the
  // admins / PMs who never upload photos themselves.
  if (items === null) return null;
  if (items.length === 0) return null;
  if (dismissed) return null;

  return (
    <Card
      className="bg-red-950/30 border-red-900 mb-6"
      data-testid="my-retakes-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-2 rounded-md border border-red-800 bg-red-950/60 text-red-300 shrink-0">
              <ShieldX className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-red-300 font-display font-bold">
                My Retakes
              </div>
              <div className="font-display font-black text-2xl tracking-tight leading-tight">
                {items.length} photo{items.length === 1 ? "" : "s"} to reshoot
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                A reviewer requested a retake — tap a card to jump into the project.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              sessionStorage.setItem(DISMISS_KEY, "1");
              setDismissed(true);
            }}
            className="text-slate-400 hover:text-slate-200 p-1 shrink-0"
            aria-label="Dismiss retakes card for this session"
            data-testid="my-retakes-dismiss"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="my-retakes-list">
          {items.map((it) => (
            <Link
              key={it.photo_id}
              to={`/projects/${it.project_id}`}
              className="flex items-center gap-3 p-2.5 rounded-md border border-red-900/70 bg-slate-950/60 hover:bg-slate-900 transition-colors group"
              data-testid={`my-retakes-item-${it.photo_id}`}
            >
              {/* Thumbnail — falls back to a camera icon on 404 (e.g.
                  stale storage path). Fixed size so the row heights
                  match even when the image is missing. */}
              <div className="w-14 h-14 rounded overflow-hidden bg-slate-900 border border-red-900/70 grid place-items-center shrink-0 relative">
                <img
                  src={fileUrl(it.photo_id, { size: "thumb" })}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <Camera className="w-4 h-4 text-slate-600 absolute" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-display font-bold text-slate-100 truncate">
                  {it.site_number || "—"} · {it.site_name || "Project"}
                </div>
                <div className="text-xs text-red-300 truncate italic">
                  {it.review_reason ? `“${it.review_reason}”` : "Retake requested"}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                  {(it.category || "Photo")} · {relTime(it.reviewed_at)}
                  {it.reviewed_by_name && ` · by ${it.reviewed_by_name}`}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-200 shrink-0" />
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
